import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL;

class ApiService {
    constructor() {
        this.api = axios.create({
            baseURL: API_BASE_URL,
            headers: {
                'Content-Type': 'application/json',
            },
        });

        // Add auth token to requests if available
        this.api.interceptors.request.use((config) => {
            const token = localStorage.getItem('authToken');
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        });

        // Handle auth errors
        this.api.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401) {
                    localStorage.removeItem('authToken');
                    window.location.href = '/login';
                }
                return Promise.reject(error);
            }
        );
    }

    // Auth endpoints
    async login(username, password) {
        const response = await this.api.post('/api/auth/login', { username, password });
        if (response.data.token) {
            localStorage.setItem('authToken', response.data.token);
        }
        return response.data;
    }

    async logout() {
        try {
            await this.api.post('/api/auth/logout');
        } finally {
            localStorage.removeItem('authToken');
        }
    }

    async verifyAuth() {
        const response = await this.api.get('/api/auth/verify');
        return response.data;
    }

    // User endpoints
    async getUser(username) {
        const response = await this.api.get(`/api/users/${username}`);
        return response.data.data;
    }

    async getUsers(filters = {}) {
        const params = new URLSearchParams();
        Object.keys(filters).forEach(key => {
            if (filters[key] !== undefined && filters[key] !== '') {
                // Handle date objects
                if (filters[key] instanceof Date) {
                    params.append(key, filters[key].toISOString());
                } else {
                    params.append(key, filters[key]);
                }
            }
        });

        const response = await this.api.get(`/api/users?${params.toString()}`);
        return response.data.data;
    }

    async createUser(userData) {
        const response = await this.api.post('/api/users', userData);
        return response.data.data;
    }

    async updatePriorities(usernames) {
        const response = await this.api.put('/api/users/priorities', { usernames });
        return response.data;
    }

    async updateUser(username, updates) {
        const response = await this.api.put(`/api/users/${username}`, updates);
        return response.data;
    }

    async deleteUser(username) {
        const response = await this.api.delete(`/api/users/${username}`);
        return response.data;
    }

    // Lead endpoints
    async getLeads(filters = {}) {
        const params = new URLSearchParams();
        Object.keys(filters).forEach(key => {
            if (filters[key] !== undefined && filters[key] !== '') {
                params.append(key, filters[key]);
            }
        });

        const response = await this.api.get(`/api/leads?${params.toString()}`);
        return response.data;
    }

    async getLeadStats(filters = {}) {
        const params = new URLSearchParams();
        Object.keys(filters).forEach(key => {
            if (filters[key] !== undefined && filters[key] !== '') {
                params.append(key, filters[key]);
            }
        });

        const response = await this.api.get(`/api/leads/stats?${params.toString()}`);
        return response.data.data;
    }

    // Config endpoints
    async getConfig() {
        const response = await this.api.get('/api/config');
        return response.data.data;
    }

    async updateConfig(configData) {
        const response = await this.api.put('/api/config', configData);
        return response.data;
    }

    // Log endpoints
    async getLogs(filters = {}) {
        const params = new URLSearchParams();
        Object.keys(filters).forEach(key => {
            if (filters[key] !== undefined && filters[key] !== '') {
                // Handle date objects
                if (filters[key] instanceof Date) {
                    params.append(key, filters[key].toISOString());
                } else {
                    params.append(key, filters[key]);
                }
            }
        });

        const response = await this.api.get(`/api/logs?${params.toString()}`);
        return response.data;
    }

    async getNewLogs(logId, filters = {}) {
        const params = new URLSearchParams();
        if (logId) {
            params.append('logId', logId);
        }
        Object.keys(filters).forEach(key => {
            if (filters[key] !== undefined && filters[key] !== '') {
                // Handle date objects
                if (filters[key] instanceof Date) {
                    params.append(key, filters[key].toISOString());
                } else {
                    params.append(key, filters[key]);
                }
            }
        });

        const response = await this.api.get(`/api/logs/new?${params.toString()}`);
        return response.data;
    }

    async getOldLogs(logId, filters = {}, limit = 50) {
        const params = new URLSearchParams();
        if (logId) {
            params.append('logId', logId);
        }
        params.append('limit', limit.toString());
        Object.keys(filters).forEach(key => {
            if (filters[key] !== undefined && filters[key] !== '') {
                // Handle date objects
                if (filters[key] instanceof Date) {
                    params.append(key, filters[key].toISOString());
                } else {
                    params.append(key, filters[key]);
                }
            }
        });

        const response = await this.api.get(`/api/logs/old?${params.toString()}`);
        return response.data;
    }

    // System status
    async getStatus() {
        const response = await this.api.get('/status');
        return response.data;
    }
}

// Export singleton instance
export default new ApiService();
